import React, { PropsWithChildren } from 'react';
import { Navigate } from 'react-router-dom';

import Routes from 'config/routes';
import { Module_Enum } from 'generated/graphql';
import { NotFound } from 'technical/router/switch/reporting-no-match';

import { useAppContext } from './contextProviders/useAppContext';
import {
  getEnabledModuleList,
  usePermissions,
} from './contextProviders/usePermissions';
import { ROLES } from './user/services/config';
import { userHasRole } from './user/services/permissions';

interface ProtectedRouteWithPermissionsProps {
  allowedRoles?: ROLES[];
  allowedModule: Module_Enum;
  redirectPath?: string;
  children: React.ReactNode;
}

const DEFAULT_NOT_CONNECTED_ROUTE = Routes.App;

const handleDataManager = (
  allowedModule: Module_Enum,
  enabledModules: Module_Enum[],
  _redirectPath: string,
  children: React.ReactNode,
) => {
  if (enabledModules.includes(allowedModule)) {
    return <>{children}</>;
  }

  // No redirect because of the PDF generation
  //
  // During PDF generation, a data-manager user will connect with an empty
  // session. The first construction site in the list will be selected. As it
  // may not have the required module there are special layout (e.g.
  // ReportConnectedPage, RingComplianceConnectedPage) which allow the
  // construction site switch based on the resource id. If we redirect to
  // NOT_FOUND route here, the redirect occurs before the construction site
  // switch.
  //
  // Since data-manager have access to any construction site, it's not a problem
  // to only display the NotFound component without redirect.
  return <NotFound />;
};

const ProtectedRouteWithPermissions = ({
  allowedRoles = [],
  allowedModule,
  redirectPath = Routes.NOT_FOUND,
  children,
}: PropsWithChildren<ProtectedRouteWithPermissionsProps>) => {
  const { user, currentConstructionSite } = useAppContext();
  const { isConnected } = useAppContext();
  const { isDataManager } = usePermissions();

  if (!isConnected || !currentConstructionSite) {
    return <Navigate to={DEFAULT_NOT_CONNECTED_ROUTE} />;
  }

  const constructionSiteEnabledModules = getEnabledModuleList(
    isDataManager,
    currentConstructionSite,
  );

  if (isDataManager) {
    return handleDataManager(
      allowedModule,
      constructionSiteEnabledModules,
      redirectPath,
      children,
    );
  }

  const validRole = allowedRoles.some((role) =>
    userHasRole(role, currentConstructionSite, user, allowedModule),
  );

  if (validRole) {
    return <>{children}</>;
  }

  return <Navigate to={redirectPath} />;
};
export default ProtectedRouteWithPermissions;
